<template>
  <form class="flex flex-col">
    <JoszakiInput
      v-model.trim="name"
      :label="
        $t('registration.name.title', {
          highlight: $t('registration.name.titleHighlight'),
        })
      "
      :placeholder="$t('registration.name.placeholder')"
      :validation-state="v$.name"
      @focusin="onNameFocus"
      @input="onNameInput"
    />

    <JoszakiInput
      v-model.trim="email"
      type="email"
      :label="
        $t('registration.email.title', {
          highlight: $t('registration.email.titleHighlight'),
        })
      "
      :placeholder="$t('registration.email.placeholder')"
      :validation-state="v$.email"
    />

    <JoszakiPhoneInput
      ref="phoneInput"
      v-model="phone"
      :label="
        $t('registration.phone.title', {
          highlight: $t('registration.phone.titleHighlight'),
        })
      "
      :placeholder="$t('registration.phone.placeholder')"
      :validation-state="v$.phone"
      professional-only
      mobile-only
    />

    <JoszakiInput
      v-model="password"
      :validation-state="v$.password"
      :label="
        $t('registration.password.title', {
          highlight: $t('registration.password.titleHighlight'),
        })
      "
      :placeholder="$t('registration.password.placeholder')"
      type="password"
      class="mb-2"
    />

    <JoszakiCheckbox v-model="terms" class="!mb-0" :validation-state="v$.terms">
      <template #label>
        <i18n path="registration.email.genericTerms.text">
          <template #link>
            <NuxtLink
              :to="
                localePath({
                  name: 'documentsTypePage',
                  params: { type: typeSlug('termsOfService') },
                })
              "
              target="_blank"
              >{{ $t("registration.email.genericTerms.link") }}</NuxtLink
            >
          </template>
        </i18n>
      </template>
    </JoszakiCheckbox>
    <JoszakiCheckbox
      v-model="dataTerms"
      class="!mb-0"
      :validation-state="v$.dataTerms"
    >
      <template #label>
        <i18n path="registration.email.dataSafetyTerms.text">
          <template #link>
            <NuxtLink
              :to="
                localePath({
                  name: 'documentsTypePage',
                  params: { type: typeSlug('privacyPolicy') },
                })
              "
              target="_blank"
              >{{ $t("registration.email.dataSafetyTerms.link") }}</NuxtLink
            >
          </template>
        </i18n>
      </template>
    </JoszakiCheckbox>

    <JoszakiButton
      :is-submit="true"
      :expanded="true"
      size="lg"
      :loading="submitting"
      class="mb-4 mt-auto"
      @click="submitRegistration"
    >
      {{ $t("registration.registrationButton") }}
    </JoszakiButton>

    <EmailExistsModal ref="emailExistsModal" />
    <MobileExistsModal ref="mobileExistsModal" />
    <CustomerLoginRequiredModal ref="customerLoginRequiredModal" />
  </form>
</template>

<script>
import { useContext, ref, useRouter } from "@nuxtjs/composition-api";
import { useRegistration } from "~/components/_refactored/registration/useRegistration";
import { useRegistrationExistenceChecks } from "~/components/_refactored/registration/useRegistrationExistenceChecks";
import EmailExistsModal from "~/components/_refactored/registration/EmailExistsModal.vue";
import MobileExistsModal from "~/components/_refactored/registration/MobileExistsModal.vue";
import CustomerLoginRequiredModal from "~/components/_refactored/registration/CustomerLoginRequiredModal.vue";
import { getLegalDocumentTypeSlug } from "~/config/legalDocumentTypes";
import { useSettingsStore } from "~/stores/settings";

export default {
  name: "SimpleRegistrationForm",
  components: { EmailExistsModal, MobileExistsModal, CustomerLoginRequiredModal },
  props: {},
  setup() {
    const { $joszaki, $trackers, i18n, localePath } = useContext();
    const router = useRouter();
    const registration = useRegistration();
    const { checkEmailExists, checkMobileExists } =
      useRegistrationExistenceChecks();
    const submitting = ref(false);
    const settingsStore = useSettingsStore();
    const typeSlug = (type) => getLegalDocumentTypeSlug(type, i18n);
    const nameFocusTracked = ref(false);
    const nameInputTracked = ref(false);
    const emailExistsModal = ref(null);
    const mobileExistsModal = ref(null);
    const customerLoginRequiredModal = ref(null);

    registration.resetForm();

    registration.city.value = {
      id: settingsStore.capitalCityId,
      name: "",
    };
    registration.type.value =
      registration.RegistrationType.PRICE_OFFER_PROFESSIONAL;

    const submitRegistration = async () => {
      const valid = await registration.v$.value.$validate();

      if (!valid) {
        return;
      }

      try {
        submitting.value = true;

        if (await checkEmailExists(registration.email.value)) {
          emailExistsModal.value.open();
          return;
        }

        if (
          await checkMobileExists({
            number: registration.phone.value.number,
            countryCodeId: registration.phone.value.countryCode?.id,
          })
        ) {
          mobileExistsModal.value.open();
          return;
        }

        await registration.submitPerson();
        router.push(
          localePath({
            name: "myPriceOffersIndexPage",
          })
        );
      } catch (error) {
        if (registration.isCustomerLoginRequiredError(error)) {
          customerLoginRequiredModal.value.open();
          return;
        }
        const message =
          typeof error === "string"
            ? error
            : i18n.t("registration.failedTryAgain");
        $joszaki.alert({
          title: i18n.t("registration.errorTitle"),
          message,
          type: "error",
        });
      } finally {
        submitting.value = false;
      }
    };

    const onNameFocus = () => {
      if (nameFocusTracked.value) return;
      nameFocusTracked.value = true;
      $trackers.trackClick("SimpleRegistrationFormNameFocus");
    };

    const onNameInput = () => {
      if (nameInputTracked.value) return;
      nameInputTracked.value = true;
      $trackers.trackClick("SimpleRegistrationFormNameInput");
    };

    return {
      ...registration,
      submitRegistration,
      submitting,
      typeSlug,
      onNameFocus,
      onNameInput,
      emailExistsModal,
      mobileExistsModal,
      customerLoginRequiredModal,
    };
  },
};
</script>
